import { keyBy } from 'lodash'
import { useMemo } from 'react'

import useIsAdmin from '~/auth/hooks/useIsAdmin'
import { useIncludeDeletedVehicles } from '~/configs/statsig'
import type { TimeRange } from '~/data/time/types'
import type { SynopUser } from '~/data/types'
import type { TariffCurrency } from '~/data/types/currencies'
import { type OrgPath, isSharedOrg } from '~/data/types/organization'
import getCurrencyForLocale, { type CurrencyCode, mapCurrencyToTariffSubset } from '~/data/utils/currency'
import { toPrimaryUptimeDisplay } from '~/data/utils/uptime'
import { useCloakingInfo, useCurrentOrgId, usePrincipalOrgId } from '~/store/slices/currentOrg'

import type { Customer, GetOrganizationCustomersArgs, GetOrganizationUsersArgs } from '../client/fleet-management'
import {
  useGetAllOcppTagsQuery,
  useGetCustomersQuery,
  useGetFleets1Query,
  useGetFleetsQuery,
  useGetOrganizationCustomersQuery,
  useGetOrganizationQuery,
  useGetOrganizationUsersQuery,
  useGetTrips1Query
} from '../client/fleet-management'
import type { UsePollingOptions, UseQueryOptions } from './polling'
import { usePolling } from './polling'
import { useCurrentUser } from './users'
import { combineSkip, useTransformedDict, useValidatedDateRange } from './utils'

/******************************************************************************
 * GET /organizations/{orgId}
 ******************************************************************************/
export function usePolledOrg(orgId?: string, options?: UsePollingOptions) {
  const result = usePolling(useGetOrganizationQuery, { id: orgId ?? '' }, combineSkip(!orgId, options))
  return { org: result.data, ...result }
}

export function useGetOrg(orgId?: string, options?: UseQueryOptions) {
  return usePolledOrg(orgId, { intervalSeconds: 0, ...options })
}

export function useCurrentOrg(options?: UseQueryOptions) {
  return useGetOrg(useCurrentOrgId(), options)
}

export function useActualOrg() {
  const { synopUser } = useCurrentUser()
  return useGetOrg(synopUser?.organizationId ?? '')
}

export function useActualOrgAllowsUserManagment() {
  const { org } = useActualOrg()
  return org?.allowUserManagement
}

export function useSsoConnectionAllowsUserManagment() {
  const { org } = useCurrentOrg()
  if (org?.ssoConnectionId && !org.allowOutsideEmails) return false
  return true
}

export function useIsCurrentOrgChargeManaged() {
  const { hasChargeManagement } = useCurrentOrg()?.org ?? {}
  return Boolean(hasChargeManagement)
}

export function useCurrentOrgHasAutopilot() {
  const { hasSynopAutopilot } = useCurrentOrg()?.org ?? {}
  return Boolean(hasSynopAutopilot)
}

/**
 * Whether the current org can reach Workflows — granted per org via the
 * `hasWorkflows` flag.
 *
 * Route guards render a loading state while `isLoading` and a not-found page
 * once it clears, so the flag stays true only while the answer is still
 * unknown.
 */
export function useWorkflowsEnabled() {
  const { org, isLoading } = useCurrentOrg()
  const enabled = !!org?.hasWorkflows
  return { enabled, isLoading: !enabled && isLoading }
}

/**
 * Whether the current org can reach Operations — granted per org via the
 * `hasOperations` flag. Same rule as `useWorkflowsEnabled`: the org flag
 * alone, with no extra gate.
 *
 * Route guards render a loading state while `isLoading` and a not-found page
 * once it clears, so the flag stays true only while the answer is still
 * unknown. A parent can grant the feature to a child only when this is true.
 */
export function useOperationsEnabled() {
  const { org, isLoading } = useCurrentOrg()
  const enabled = !!org?.hasOperations
  return { enabled, isLoading: !enabled && isLoading }
}

/******************************************************************************
 * GET /organizations/{orgId}/customers
 ******************************************************************************/
type UseChildOrgArgs = Omit<GetOrganizationCustomersArgs, 'id'>
export function useGetPolledChildOrgs(orgId: string, args?: UseChildOrgArgs, options?: UsePollingOptions) {
  const result = usePolling(useGetOrganizationCustomersQuery, { id: orgId, ...args }, combineSkip(!orgId, options))
  return { organizations: result.data, ...result }
}

export function useGetChildOrgs(orgId: string, args?: UseChildOrgArgs, options?: UseQueryOptions) {
  return useGetPolledChildOrgs(orgId, args, { intervalSeconds: 0, ...options })
}

export function useCurrentOrgChildOrgs(args?: UseChildOrgArgs, options?: UseQueryOptions) {
  return useGetChildOrgs(useCurrentOrgId(), args, options)
}

export function useGetPolledCustomers(orgId: string, options?: UsePollingOptions) {
  const { useCache, ...pollingOptions } = options ?? {}
  const result = usePolling(
    useGetCustomersQuery,
    { organizationId: orgId, page: 0, count: 2500 },
    combineSkip(!orgId, pollingOptions)
  )

  // When useCache is true, prefer cached data.
  // Cached reference will update as fresh data loads.
  const customers = useCache && result.currentData?.data ? result.currentData.data : result.data?.data
  const effectiveIsLoading = useCache ? result.isLoading && !result.currentData?.data : result.isLoading

  return { customers, ...result, isLoading: effectiveIsLoading }
}

export function useGetCustomers(orgId: string, options?: UseQueryOptions) {
  return useGetPolledCustomers(orgId, { intervalSeconds: 0, ...options })
}

/**
 * Child organizations of the current org — admin-only (ENG-11703), so org
 * pickers and name joins built on this hook cannot surface descendant org names
 * to Driver / Fleet Operator users.
 *
 * This withholds the list from the *UI*, not from the browser: the data manager
 * fetches the same endpoint at `depth: 100` for every user, admin or not, and
 * persists it to IndexedDB (`manager/organizations.ts`). Skipping here only
 * avoids a second, narrower request — it is not a confidentiality boundary, and
 * anything that needs to be one has to be enforced by the backend.
 *
 * Non-admins reach org names through `useVisibleOrgs`, which resolves only the
 * orgs their site grants cover.
 */
export function useCurrentOrgCustomers(options?: UseQueryOptions) {
  const isAdmin = useIsAdmin()
  const result = useGetCustomers(useCurrentOrgId(), {
    ...options,
    skip: !isAdmin || Boolean(options?.skip)
  })

  if (!isAdmin) {
    // Clear every payload field — `useMeters` and similar consumers historically read
    // `currentData` / `data`, not only `customers`. Leaving those through would either
    // leak names from a stale cache or (when the query is skipped) look like a failed
    // tree and silently collapse org scope.
    return {
      ...result,
      customers: [],
      currentData: undefined,
      data: undefined,
      isLoading: false,
      isFetching: false
    }
  }

  return result
}

export function useCurrentOrgAndCustomers(options?: UseQueryOptions) {
  const currentOrgId = useCurrentOrgId()
  const { org } = useGetOrg(currentOrgId)
  const result = useCurrentOrgCustomers(options)

  const combinations = useMemo(() => {
    // Admins: wait for the customers payload before combining (undefined while
    // loading). Non-admins get `customers: []` immediately from
    // useCurrentOrgCustomers, so this resolves to just the current org.
    const combinedOrgs = org && result.customers ? [org, ...result.customers] : []
    const orgMap = keyBy(combinedOrgs, 'id')
    return { combinedOrgs, orgMap }
  }, [org, result.customers])

  return { ...combinations, ...result }
}

export function useActualOrgAndCustomers(options?: UseQueryOptions) {
  const { synopUser } = useCurrentUser()
  const actualOrgId = synopUser?.organizationId ?? ''
  const { org } = useGetOrg(actualOrgId)
  const result = useGetCustomers(actualOrgId, options)

  const combinations = useMemo(() => {
    const combinedOrgs = org && result.customers ? [org, ...result.customers] : []
    const orgMap = keyBy(combinedOrgs, 'id')
    return { combinedOrgs, orgMap }
  }, [org, result.customers])

  return { ...combinations, ...result }
}

/******************************************************************************
 * GET /organizations/{orgId}/users
 ******************************************************************************/
type UseOrgUsersArgs = Omit<GetOrganizationUsersArgs, 'id'>
export function useGetPolledOrgUsers(orgId: string, args?: UseOrgUsersArgs, options?: UsePollingOptions) {
  const result = usePolling(useGetOrganizationUsersQuery, { id: orgId, ...args }, combineSkip(!orgId, options))
  return { users: result.data as SynopUser[] | undefined, ...result }
}

export function useGetOrgUsers(orgId: string, args?: UseOrgUsersArgs) {
  return useGetPolledOrgUsers(orgId, args, { intervalSeconds: 0 })
}

export function useGetCurrentOrgUsers(args?: UseOrgUsersArgs) {
  const currentOrgId = useCurrentOrgId()
  return useGetPolledOrgUsers(currentOrgId, args)
}

export function useGetCurrentOrgUsersMap(args?: UseOrgUsersArgs) {
  const { users, isLoading } = useGetCurrentOrgUsers(args)
  return useMemo(() => {
    return { users: keyBy(users, 'id'), isLoading }
  }, [users, isLoading])
}

/******************************************************************************
 * GET /organizations/{orgId}/fleets
 ******************************************************************************/
export function usePolledOrgFleets(orgId: string, options?: UsePollingOptions) {
  const result = usePolling(useGetFleets1Query, { id: orgId }, combineSkip(!orgId, options))
  return { fleets: result.data, ...result }
}

export function useOrgFleets(orgId: string) {
  return usePolledOrgFleets(orgId, { intervalSeconds: 0 })
}

export function useCurrentOrgFleets() {
  const currentOrgId = useCurrentOrgId()
  return usePolledOrgFleets(currentOrgId)
}

/******************************************************************************
 * GET /fleets?organizationId={orgId}
 *
 * Hierarchy-wide (current org + descendants), unlike `useOrgFleets` which only
 * returns fleets owned directly by the given org via GET /organizations/{id}/fleets.
 ******************************************************************************/
export function useAllOrgFleets(orgId: string, options?: UseQueryOptions) {
  const result = useGetFleetsQuery(
    { organizationId: orgId, page: 0, count: 1000 },
    { skip: !orgId, ...options }
  )
  return { fleets: result.data?.data, ...result }
}

export function useCurrentOrgAllFleets(options?: UseQueryOptions) {
  return useAllOrgFleets(useCurrentOrgId(), options)
}

/******************************************************************************
 * GET /organizations/{orgId}/trips
 ******************************************************************************/
export function useOrgTrips(organizationId: string, timeRange: TimeRange, vehicleIds: string[]) {
  const { from, to } = useValidatedDateRange(timeRange)
  const result = usePolling(
    useGetTrips1Query,
    { id: organizationId, from: from.time, to: to.time, vehicleIds },
    { skip: !organizationId || !from.valid || !to.valid || vehicleIds.length === 0, intervalSeconds: 0 }
  )

  return { trips: result.data, ...result }
}

/******************************************************************************
 * COMBO
 ******************************************************************************/

export function useGetOrgAndChildOrgs(
  orgId: string,
  args?: Omit<GetOrganizationCustomersArgs, 'id'>,
  options?: UseQueryOptions
) {
  const { org, isLoading: isOrgsLoading } = useGetOrg(orgId)
  const { organizations, isLoading: isCustomersLoading } = useGetChildOrgs(orgId, args, options)

  const combinedOrgs = useMemo(() => {
    if (!org || !organizations) return undefined
    return [org, ...organizations]
  }, [org, organizations])

  return { orgs: combinedOrgs, isLoading: isOrgsLoading || isCustomersLoading }
}

/**
 * Current org plus its children. Non-admins only receive the current org
 * (ENG-11703) — child rows are never fetched or joined for name display.
 */
export function useGetCurrentOrgAndChildOrgs(args?: Omit<GetOrganizationCustomersArgs, 'id'>) {
  const isAdmin = useIsAdmin()
  const currentOrgId = useCurrentOrgId()
  const { org, isLoading: isOrgsLoading } = useGetOrg(currentOrgId)
  const { organizations, isLoading: isCustomersLoading } = useGetChildOrgs(currentOrgId, args, {
    skip: !isAdmin
  })

  const combinedOrgs = useMemo(() => {
    if (!org) return undefined
    if (!isAdmin) return [org]
    if (!organizations) return undefined
    return [org, ...organizations]
  }, [org, organizations, isAdmin])

  return { orgs: combinedOrgs, isLoading: isOrgsLoading || (isAdmin && isCustomersLoading) }
}

export function useGetCurrentOrgAndChildOrgsMap(args?: Omit<GetOrganizationCustomersArgs, 'id'>) {
  const { orgs, isLoading } = useGetCurrentOrgAndChildOrgs(args)
  return useMemo(() => {
    return { orgs: keyBy(orgs, 'id'), isLoading }
  }, [orgs, isLoading])
}

const AUTH_TAG_PREFIXES = ['VID:', 'EVCCID:']

/**
 * Returns a map of OCPP tag value to OCPP Tags for users and vehicles
 * @param maybeOrgId
 * @returns
 */
export function useOrgOcppTagsMap(maybeOrgId?: string) {
  const currentOrgId = useCurrentOrgId()
  const orgId = maybeOrgId ?? currentOrgId
  // Include deleted vehicles' tags so historical transactions/line-items authorized by a
  // since-offboarded vehicle still resolve its entity name (ENG-9535, gated by Statsig).
  const includeDeleted = useIncludeDeletedVehicles()
  const { data: idTags } = useGetAllOcppTagsQuery({ id: orgId, includeDeleted }, { skip: !orgId })

  const tagMap = useMemo(() => keyBy(idTags, 'tagId'), [idTags])
  return useTransformedDict(tagMap, { caseInsensitive: true, prefix: AUTH_TAG_PREFIXES, separator: ':' })
}

export function useCurrentOrgHasInsights() {
  const { org } = useCurrentOrg()
  return org?.hasInsights
}

/**
 * Whether the current org shows charger health (Needs Attention & Uptime) data.
 * Backed by the org's `hasReporting` flag (repurposed for the Charger Health feature).
 * Defaults to enabled — only an explicit `false` (parent opted out of sharing with
 * this child org) hides it.
 *
 * `isLoading` is surfaced so callers can hold the charger-health UI (and its polling)
 * until the org resolves: while loading, `org` is undefined and `hasReporting !== false`
 * would otherwise read `true`, briefly exposing the data for an opted-out org.
 */
export function useCurrentOrgHasChargerHealth() {
  const { org, isLoading } = useCurrentOrg()
  return { showChargerHealth: org?.hasReporting !== false, isLoading: isLoading || !org }
}

/**
 * Which uptime metric the current org displays as "the" uptime across the platform.
 *
 * The backend resolves INHERIT up the org's parent chain into
 * `effectivePrimaryUptimeDisplay` (never INHERIT); before that field is deployed it is
 * absent and we fall back to the platform default (the historical metric).
 * `canEdit` mirrors the backend's `canEditPrimaryUptimeDisplay` — ancestors and top-level
 * orgs may change the setting; sub-orgs see it read-only. It requires an explicit `true`, so a
 * missing flag fails safe to read-only: a sub-org can never gain edit rights (and override its
 * parent's INHERIT) just because the flag was absent from the response.
 */
export function useCurrentOrgPrimaryUptimeDisplay() {
  const { org, isLoading } = useCurrentOrg()
  const primaryUptimeDisplay = toPrimaryUptimeDisplay(org?.effectivePrimaryUptimeDisplay)
  return { primaryUptimeDisplay, canEdit: org?.canEditPrimaryUptimeDisplay === true, isLoading: isLoading || !org }
}

/******************************************************************************
 * ORGANIZATION TREE STRUCTURE
 ******************************************************************************/

/**
 * Hook to get organizations with parent path information
 */
export function useOrganizationTree(options?: UseQueryOptions) {
  const { customers, currentData, isLoading, isFetching, isError } = useCurrentOrgCustomers(options)
  const principalOrgId = usePrincipalOrgId()
  const { org: principalOrg } = useGetOrg(principalOrgId, {
    skip: Boolean(options?.skip) || !principalOrgId
  })
  const { cloakedOrgs } = useCloakingInfo()

  // Bind to the current org's query arg (same pattern as `useMeters`). After
  // cloak or Exit, `customers`/`data` can still be the previous org while
  // `isLoading` is already false; `currentData` is undefined until this org
  // settles — treat that gap as loading so the hierarchy bar / Owned by column
  // don't flash empty/stale descendants.
  const settledCustomers =
    currentData?.data !== undefined ? currentData.data : isFetching ? undefined : customers
  const treeLoading = Boolean(isLoading) || (Boolean(isFetching) && currentData?.data === undefined)

  const organizationsWithPaths = useMemo(() => {
    if (!settledCustomers?.length) return []

    // Create a map of ID -> Customer for quick lookup
    const orgMap = keyBy(settledCustomers, 'id')

    // Include all orgs in the cloaking chain so path traversal works through the full hierarchy
    // When cloaked (e.g., Parent → Child → viewing Grandchild), we need Parent and Child in the map
    // to build the complete path [Parent, Child, Grandchild]
    cloakedOrgs.forEach((cloakedOrg, index) => {
      if (cloakedOrg.id && !orgMap[cloakedOrg.id]) {
        const parentOrg = index > 0 ? cloakedOrgs[index - 1] : undefined
        orgMap[cloakedOrg.id] = {
          id: cloakedOrg.id,
          organizationNm: cloakedOrg.name,
          parentOrganizationId: parentOrg?.id
        }
      }
    })

    // Helper function to build the full path for an organization
    const buildPath = (org: Customer) => {
      // Shared orgs sit outside the real hierarchy. The natural breadcrumb
      // for them is just [principal, sharedOrg] — the principal is the org
      // that received the share, and the shared org's true parent lives in
      // another tenancy that this user can't see.
      if (isSharedOrg(org)) {
        const path: OrgPath = []
        if (principalOrg?.id) {
          path.push({ id: principalOrg.id, name: principalOrg.organizationNm ?? '' })
        }
        path.push({ id: org.id!, name: org.organizationNm ?? '' })
        return path
      }

      const path: OrgPath = []
      let current: Customer | undefined = org

      // Traverse up the parent chain to build the path
      while (current) {
        // Add to front to get root-to-leaf order
        path.unshift({ id: current.id!, name: current.organizationNm ?? '' })

        if (current.parentOrganizationId && current.parentOrganizationId === principalOrg?.id) {
          path.unshift({ id: principalOrg.id, name: principalOrg.organizationNm ?? '' })
          break
        }

        current = current.parentOrganizationId ? orgMap[current.parentOrganizationId] : undefined
      }

      return path
    }

    // Create the result with computed paths
    return settledCustomers.map((org) => ({ ...org, path: buildPath(org) }))
  }, [settledCustomers, cloakedOrgs, principalOrg])

  return {
    organizations: organizationsWithPaths,
    isLoading: treeLoading,
    isError: Boolean(isError)
  }
}

export function usePreferredOrgCurrency(options: { subset: 'tariff' }): TariffCurrency
export function usePreferredOrgCurrency(options?: { subset?: 'billing' }): CurrencyCode
export function usePreferredOrgCurrency(options?: { subset?: 'tariff' | 'billing' }) {
  const { org } = useCurrentOrg()
  const fullCurrency = getCurrencyForLocale(org?.locale)

  if (options?.subset === 'tariff') {
    return mapCurrencyToTariffSubset(fullCurrency)
  }

  return fullCurrency
}
